export interface HttpErrorDetails {
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly method: string | undefined;
  readonly url: string | undefined;
  readonly data: unknown;
  readonly cause?: unknown;
  // Per validation-error shape: one entry per invalid field, or plain string message.
  readonly messages?: readonly string[];
  // Response headers, lower-cased -- e.g. reading `retry-after` off a 429.
  readonly headers?: Record<string, string>;
}

// Transport layer error; repositories and retry logic depend on this stable interface.
export class HttpError extends Error {
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly method: string | undefined;
  readonly url: string | undefined;
  readonly data: unknown;
  readonly messages: readonly string[];
  readonly headers: Record<string, string> | undefined;

  constructor(message: string, details: HttpErrorDetails) {
    super(message, { cause: details.cause });
    this.name = "HttpError";
    this.status = details.status;
    this.code = details.code;
    this.method = details.method;
    this.url = details.url;
    this.data = details.data;
    this.messages = details.messages ?? [message];
    this.headers = details.headers;
  }
}

/**
 * Seconds until a rate-limited request may be retried, per the standard
 * `Retry-After` header (RFC 9110 §10.2.3) -- either delta-seconds or an
 * HTTP-date. Returns `undefined` when absent or unparseable, so callers can
 * fall back to a sane default rather than a broken countdown.
 */
export function retryAfterSeconds(error: HttpError): number | undefined {
  const value = error.headers?.["retry-after"];
  if (value === undefined) return undefined;

  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);

  const date = Date.parse(value);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.ceil((date - Date.now()) / 1000));
}
